/**
 * The generative summary of Pulse (« Ta journée »): a small json-render catalog (vercel-labs/json-render) shared by
 * the Worker (system prompt via `catalog.prompt()`, validation of the model's spec) and the web app (the registry that
 * draws each component). A spec is the flat json-render tree `{ root, elements: { id: { type, props, children, on } } }`.
 *
 * Every text prop has a tight maximum length. Actions are limited to two: `ask` (puts a message in the chat composer)
 * and `open` (goes to an in-app page from a closed list). `sanitizeSummarySpec` keeps only what this catalog allows:
 * unknown types, props that fail their schema, `$state` expressions, visibility/repeat/watch fields, dangling or
 * cyclic children and children of the wrong kind are dropped; it returns null when nothing worth showing is left.
 */
import { defineCatalog } from "@json-render/core";
import { schema } from "@json-render/react/schema";
import { z } from "zod";

/** A text prop: trimmed, never empty, at most `max` characters. */
export const uiText = (max: number) => z.string().trim().min(1).max(max);
const text = uiText;
export const UI_TONES = ["neutral", "accent", "warning", "success"] as const;
const tone = z.enum(UI_TONES).nullish();
export const UI_SKIES = ["sun", "cloud", "rain", "storm", "snow", "fog", "wind", "mixed"] as const;

/**
 * Component props that the chat's generated interface (src/shared/chat-ui.ts) shares with this catalog, identical in
 * both (Stat is not: the chat's value takes 10 characters).
 */
export const SHARED_UI_PROPS = {
  Stack: z.object({ gap: z.enum(["sm", "md"]).nullish() }),
  Row: z.object({}),
  Headline: z.object({ text: text(90) }),
  DayTimeline: z.object({ title: text(40).nullish() }),
  TimelineItem: z.object({ time: text(12), label: text(70), note: text(60).nullish(), tone }),
  Highlight: z.object({ title: text(70), text: text(120).nullish(), tone }),
  PersonChip: z.object({ name: text(40), why: text(60) }),
  Weather: z.object({ temp: text(8), sky: z.enum(UI_SKIES), hint: text(60).nullish() }),
  Progress: z.object({ label: text(60), percent: z.number().int().min(0).max(100) }),
  ActionChip: z.object({ label: text(40) }),
};
/** Params of the `ask` action, shared with the chat's catalog. */
export const ASK_PARAMS = z.object({ message: text(200) });

/** Pages an `open` action may lead to (never an outside URL). */
export const SUMMARY_ROUTES = ["/approbations", "/missions", "/discussion", "/veilles", "/memoire", "/connexions", "/ordinateur", "/agent"] as const;
export type SummaryRoute = (typeof SUMMARY_ROUTES)[number];

export const summaryCatalog = defineCatalog(schema, {
  components: {
    Stack: {
      props: SHARED_UI_PROPS.Stack,
      slots: ["default"],
      description: "Colonne verticale. Racine habituelle du résumé.",
    },
    Row: {
      props: SHARED_UI_PROPS.Row,
      slots: ["default"],
      description: "Ligne qui passe à la ligne au besoin : pour 2 à 4 Stat, PersonChip, Weather ou ActionChip côte à côte.",
    },
    Headline: {
      props: SHARED_UI_PROPS.Headline,
      description: "Une phrase d'accroche, en tête : l'essentiel de la journée en peu de mots. Au plus une.",
    },
    DayTimeline: {
      props: SHARED_UI_PROPS.DayTimeline,
      slots: ["default"],
      description: "Frise de la journée. Enfants : TimelineItem uniquement, dans l'ordre chronologique.",
    },
    TimelineItem: {
      props: SHARED_UI_PROPS.TimelineItem,
      description: "Un moment de la frise : heure courte (« 10 h », « 14 h 30 »), libellé, note facultative.",
    },
    Highlight: {
      props: SHARED_UI_PROPS.Highlight,
      slots: ["default"],
      description: "Une priorité du jour mise en avant, avec une ligne courte. Enfant facultatif : un ActionChip.",
    },
    Stat: {
      props: z.object({ value: text(8), label: text(40), tone }),
      description: "Un nombre réel tiré des données (ex. 3 · validations) avec un libellé court.",
    },
    PersonChip: {
      props: SHARED_UI_PROPS.PersonChip,
      description: "Une personne à qui penser aujourd'hui et pourquoi, en quelques mots.",
    },
    Weather: {
      props: SHARED_UI_PROPS.Weather,
      description: "La météo, seulement si les données la donnent : température, ciel, un conseil court.",
    },
    Progress: {
      props: SHARED_UI_PROPS.Progress,
      description: "Une progression réelle tirée des données (ex. 3 tâches sur 4 faites = 75). Jamais inventée.",
    },
    ActionChip: {
      props: SHARED_UI_PROPS.ActionChip,
      description: "Un bouton d'action court. Toujours avec on.press : ask (message à l'agent) ou open (page de l'app).",
    },
  },
  actions: {
    ask: {
      params: ASK_PARAMS,
      description: 'Met un message dans la discussion avec l\'agent. Params : { "message": "texte court à la première personne" }.',
    },
    open: {
      params: z.object({ to: z.enum(SUMMARY_ROUTES) }),
      description: `Ouvre une page de l'app. Params : { "to": une de ${SUMMARY_ROUTES.join(", ")} }.`,
    },
  },
});

export type SummaryComponent = keyof typeof summaryCatalog.data.components;
export type SummaryAction = { action: "ask"; params: { message: string } } | { action: "open"; params: { to: SummaryRoute } };

export interface SummaryElement {
  type: SummaryComponent;
  props: Record<string, unknown>;
  children: string[];
  on?: { press: SummaryAction };
}

export interface SummarySpec {
  root: string;
  elements: Record<string, SummaryElement>;
}

/** One suggestion chip under the composer: a short label, and the message it puts in the chat. */
export interface SummarySuggestion {
  label: string;
  message: string;
}

export interface PulseSummary {
  spec: SummarySpec;
  suggestions: SummarySuggestion[];
  generatedAt: number;
  source: "model" | "fallback";
}

export const SUGGESTION_LABEL_MAX = 40;
export const SUGGESTION_MESSAGE_MAX = 200;
/** Elements a spec may hold (layout included): the prompt asks for about ten. */
export const SUMMARY_MAX_ELEMENTS = 16;
const MAX_DEPTH = 4;

const CONTAINERS: Partial<Record<SummaryComponent, readonly SummaryComponent[]>> = {
  Stack: ["Row", "Headline", "DayTimeline", "Highlight", "Stat", "PersonChip", "Weather", "Progress", "ActionChip"],
  Row: ["Stat", "PersonChip", "Weather", "ActionChip"],
  DayTimeline: ["TimelineItem"],
  Highlight: ["ActionChip"],
};

const suggestionSchema = z.object({ label: text(SUGGESTION_LABEL_MAX), message: text(SUGGESTION_MESSAGE_MAX).nullish() });

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function componentOf(type: unknown): SummaryComponent | null {
  return typeof type === "string" && Object.hasOwn(summaryCatalog.data.components, type) ? (type as SummaryComponent) : null;
}

/** The press binding of an element, when it is exactly one allowed action with valid params. */
function pressOf(on: unknown): SummaryAction | null {
  if (!isRecord(on)) return null;
  const binding = Array.isArray(on.press) ? on.press[0] : on.press;
  if (!isRecord(binding)) return null;
  if (binding.action === "ask") {
    const p = summaryCatalog.data.actions.ask.params.safeParse(binding.params);
    return p.success ? { action: "ask", params: p.data } : null;
  }
  if (binding.action === "open") {
    const p = summaryCatalog.data.actions.open.params.safeParse(binding.params);
    return p.success ? { action: "open", params: p.data } : null;
  }
  return null;
}

/** One element checked on its own (type, props, press); children are resolved by the tree walk. */
function checkElement(raw: unknown): { type: SummaryComponent; props: Record<string, unknown>; children: string[]; press: SummaryAction | null } | null {
  if (!isRecord(raw)) return null;
  const type = componentOf(raw.type);
  if (!type) return null;
  const parsed = summaryCatalog.data.components[type].props.safeParse(raw.props ?? {});
  if (!parsed.success) return null;
  const press = pressOf(raw.on);
  if (type === "ActionChip" && !press) return null;
  const children = Array.isArray(raw.children) ? raw.children.filter((c): c is string => typeof c === "string") : [];
  return { type, props: parsed.data as Record<string, unknown>, children, press };
}

/** Leaves (not layout) of a spec: what the reader actually sees. */
export function summaryLeafCount(spec: SummarySpec): number {
  return Object.values(spec.elements).filter((e) => !CONTAINERS[e.type] || e.type === "Highlight").length;
}

/**
 * Keeps what the catalog allows (see the module comment). A leaf root is wrapped in a Stack. Returns null when the
 * input is not a spec or nothing visible survives.
 */
export function sanitizeSummarySpec(input: unknown): SummarySpec | null {
  if (!isRecord(input) || typeof input.root !== "string" || !isRecord(input.elements)) return null;
  const rawElements = input.elements;
  const out: Record<string, SummaryElement> = {};
  const seen = new Set<string>();
  let count = 0;

  const visit = (id: string, depth: number, allowed: readonly SummaryComponent[] | null): string | null => {
    if (seen.has(id) || count >= SUMMARY_MAX_ELEMENTS || depth > MAX_DEPTH || !Object.hasOwn(rawElements, id)) return null;
    const el = checkElement(rawElements[id]);
    if (!el || (allowed && !allowed.includes(el.type))) return null;
    seen.add(id);
    count += 1;
    const childTypes = CONTAINERS[el.type];
    const children = childTypes ? el.children.map((c) => visit(c, depth + 1, childTypes)).filter((c): c is string => c !== null) : [];
    // A container left empty shows nothing (a Highlight stands on its own).
    if (childTypes && el.type !== "Highlight" && children.length === 0) {
      count -= 1;
      return null;
    }
    out[id] = { type: el.type, props: el.props, children, ...(el.press ? { on: { press: el.press } } : {}) };
    return id;
  };

  const rootRaw = checkElement(rawElements[input.root]);
  let root: string | null;
  if (rootRaw && (rootRaw.type === "Stack" || rootRaw.type === "Row")) {
    root = visit(input.root, 0, null);
  } else {
    // A leaf (or a nested container) as root: wrap it.
    const child = visit(input.root, 1, CONTAINERS.Stack!);
    root = child ? "__root" : null;
    if (child) out.__root = { type: "Stack", props: {}, children: [child] };
  }
  if (!root) return null;
  const spec: SummarySpec = { root, elements: out };
  return summaryLeafCount(spec) > 0 ? spec : null;
}

/** Valid, distinct suggestions (at most 3); a missing message is the label itself. */
export function sanitizeSuggestions(input: unknown): SummarySuggestion[] {
  if (!Array.isArray(input)) return [];
  const out: SummarySuggestion[] = [];
  for (const raw of input) {
    const s = suggestionSchema.safeParse(typeof raw === "string" ? { label: raw } : raw);
    if (!s.success) continue;
    if (out.some((o) => o.label.toLowerCase() === s.data.label.toLowerCase())) continue;
    out.push({ label: s.data.label, message: s.data.message ?? s.data.label });
    if (out.length === 3) break;
  }
  return out;
}

/** Shortens to `max` characters on a word boundary when it can, with an ellipsis. */
export function clipText(s: string, max: number): string {
  const t = s.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:.–-]+$/u, "")}…`;
}
